/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import ResetPasswordFields from '@amblique/king-living/app/components/forms/reset-password-fields';
import {AlertErrorIcon} from '@amblique/king-living/app/components/icons';
import {Alert, AlertIcon, AlertTitle, Box, Button, Stack, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {noop} from '@salesforce/retail-react-app/app/utils/utils';

const ResetPasswordForm = ({submitForm, clickSignIn = noop, form}) => {
  const styles = useMultiStyleConfig('FormAuth', {});

  return (
    <Fragment>
      {!form.formState.isSubmitSuccessful ? (
        <>
          <Stack className="reset-password-header" {...styles.header} {...styles.headerResetPassword}>
            <Text {...styles.title}>
              <FormattedMessage
                defaultMessage="Reset your password"
                id="login_page.resetpassword.title"
              />
            </Text>
            <Text {...styles.description} {...styles.descriptionText}>
              <FormattedMessage
                defaultMessage="We will send you an email with instructions on how to reset your password."
                id="login_page.resetpassword.description"
              />
            </Text>
          </Stack>
          <form data-testid="sf-auth-modal-form" onSubmit={form.handleSubmit(submitForm)}>
            <Stack gap={0}>
              {form.formState.errors?.global && (
                <Alert
                  className="form-alert"
                  status="error"
                  variant="error"
                >
                  <AlertIcon as={AlertErrorIcon} />
                  <Stack flex="1" spacing={1}>
                    <AlertTitle>{form.formState.errors.global.message}</AlertTitle>
                  </Stack>
                </Alert>
              )}
              <Box pt={4}>
                <ResetPasswordFields form={form} />
              </Box>
              <Stack {...styles.buttonContainer}>
                <Button
                  isLoading={form.formState.isSubmitting}
                  type="submit"
                  onClick={() => form.clearErrors('global')}
                  {...styles.buttonSubmit}
                >
                  <FormattedMessage
                    defaultMessage="Send reset link"
                    id="reset_password_form.button.reset_link"
                  />
                </Button>

                <Text {...styles.divider}>
                  <FormattedMessage
                    defaultMessage="Or return to"
                    description="Precedes link to return to sign in"
                    id="reset_password_form.message.return_to_sign_in"
                  />
                </Text>
                <Button onClick={clickSignIn} {...styles.buttonOutline}>
                  <FormattedMessage
                    defaultMessage="Sign in"
                    id="reset_password_form.action.sign_in"
                  />
                </Button>
              </Stack>
            </Stack>
          </form>
        </>
      ) : (
        <Stack {...styles.header}>
          <Text {...styles.title}>
            <FormattedMessage
              defaultMessage="Reset your password"
              id="login_page.resetpassword.title"
            />
          </Text>
          <Text {...styles.description}>
            <FormattedMessage
              defaultMessage="You will receive an email at <b>{email}</b> with a link to reset your password shortly."
              id="auth_modal.password_reset_success.info.will_email_shortly"
              values={{
                email: form.getValues('email'),

                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </Text>

          <Button onClick={clickSignIn} {...styles.buttonSubmit}>
            <FormattedMessage
              defaultMessage="Back to sign in"
              id="auth_modal.password_reset_success.button.back_to_sign_in"
            />
          </Button>
        </Stack>
      )}
    </Fragment>
  );
};

ResetPasswordForm.propTypes = {
  submitForm: PropTypes.func,
  clickSignIn: PropTypes.func,
  form: PropTypes.object,
};

export default ResetPasswordForm;
